import React, { ReactNode, useMemo, useCallback, useEffect } from 'react';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import { Dropdown } from '../ui/Listbox';
import { SearchInput } from '../ui/SearchInput';
import Tooltip from '../ui/Tooltip';
import Empty from './Empty';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// ====================================
// TYPE DEFINITIONS
// ====================================

export interface DataTableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (item: T, index: number) => ReactNode;
  mobileRender?: (item: T, index: number) => ReactNode;
  mobileLabel?: string;
}

export interface DataTablePagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

export interface DataTableSelection<T> {
  selectedItems: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  getItemId: (item: T) => string;
  isSelectAll?: boolean;
  isIndeterminate?: boolean;
}

export interface DataTableSort {
  field: string;
  direction: 'asc' | 'desc';
  onSort: (field: string) => void;
}

export interface DataTableSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export interface DataTableBulkAction {
  id: string;
  text: string;
}

export interface DataTableBulkActionButtonConfig {
  text: string;
  variant?: 'filled' | 'outline' | 'ghost';
  color?: 'primary' | 'danger';
  icon?: ReactNode;
  loading?: boolean;
  loadingText?: string;
  successText?: string;
}

export interface DataTableBulkActions {
  actions: DataTableBulkAction[];
  selectedAction?: string | null;
  onActionChange: (actionId: string | null) => void;
  onActionExecute: (actionId: string, selectedItems: Set<string>) => void;
  secondaryOptions?: DataTableBulkAction[];
  selectedSecondaryOption?: string;
  onSecondaryOptionChange?: (optionId: string) => void;
  canPerformActions?: boolean;
  restrictionMessage?: string;
  requiresSecondarySelection?: Set<string>;
  customActionButtons?: Record<string, DataTableBulkActionButtonConfig>;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  successMessage?: string | null;
  onClearError?: () => void;
  onClearSuccessMessage?: () => void;
  emptyMessage?: string;
  pagination?: DataTablePagination;
  selection?: DataTableSelection<T>;
  sort?: DataTableSort;
  search?: DataTableSearch;
  bulkActions?: DataTableBulkActions;
  filters?: ReactNode;
  onRowClick?: (item: T) => void;
  className?: string;
  mobileCardClassName?: string;
  hoverRowClassName?: string;
  gridCols?: string;
  totalItemsCount?: number;
}

// ====================================
// UTILITY FUNCTIONS
// ====================================

const generateGridCols = (columns: DataTableColumn<any>[], hasSelection: boolean, customGridCols?: string): string => {
  if (customGridCols) return customGridCols;
  
  const selectionCol = hasSelection ? '48px' : '';
  const dataCols = columns.map(col => col.width || 'minmax(100px,1fr)').join('_');
  
  return hasSelection 
    ? `grid-cols-[${selectionCol}_${dataCols}]`
    : `grid-cols-[${dataCols}]`;
};

const calculatePaginationInfo = (pagination: DataTablePagination) => {
  const { currentPage, itemsPerPage, totalItems } = pagination;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  return {
    startIndex: startIndex + 1,
    endIndex: Math.min(endIndex, totalItems),
    showingText: `Showing ${startIndex + 1} to ${Math.min(endIndex, totalItems)} of ${totalItems} items`
  };
};

const getSelectionStats = (
  selection: DataTableSelection<any> | undefined,
  data: any[],
  totalItemsCount?: number
) => {
  if (!selection) return { selectedCount: 0, totalCount: 0, isAnySelected: false };
  
  const selectedCount = selection.selectedItems.size;
  const totalCount = totalItemsCount || data.length;
  const isAnySelected = selectedCount > 0;
  
  return { selectedCount, totalCount, isAnySelected };
};

// ====================================
// MEMOIZED COMPONENTS
// ====================================

const LoadingSkeleton = React.memo(({ hasSelection }: { hasSelection: boolean }) => (
  <div className="bg-white rounded-lg divide-y divide-gray-200">
    {[1, 2, 3].map((n) => (
      <div key={n} className="animate-pulse p-4">
        <div className="flex items-center">
          {hasSelection && <div className="w-5 h-5 bg-gray-200 rounded-sm mr-4" />}
          <div className="w-10 h-10 bg-gray-200 rounded-full mr-4" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded-sm w-1/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded-sm w-1/3" />
          </div>
          <div className="w-24 h-8 bg-gray-200 rounded-sm ml-4" />
        </div>
      </div>
    ))}
  </div>
));

const SortIcon = React.memo(({ 
  column, 
  sort 
}: { 
  column: DataTableColumn<any>, 
  sort: DataTableSort | undefined 
}) => {
  if (!column.sortable || !sort) return null;
  
  const sortKey = column.sortKey || column.key;
  if (sort.field !== sortKey) return null;
  
  return sort.direction === 'asc' ? (
    <ChevronUpIcon className="h-3 w-3 ml-1" />
  ) : (
    <ChevronDownIcon className="h-3 w-3 ml-1" />
  );
});

const TableHeader = React.memo(({ 
  columns, 
  hasSelection, 
  selection, 
  sort, 
  gridCols,
  onColumnSort
}: {
  columns: DataTableColumn<any>[];
  hasSelection: boolean;
  selection?: DataTableSelection<any>;
  sort?: DataTableSort;
  gridCols: string;
  onColumnSort: (column: DataTableColumn<any>) => void;
}) => (
  <div className="border-b border-gray-200">
    <div className={`hidden lg:grid ${gridCols} px-3 py-3 bg-gray-50 text-xs font-medium text-gray-500 tracking-wider rounded-t-lg`}>
      {hasSelection && (
        <div className="flex items-center">
          <Checkbox
            checked={selection?.isSelectAll || false}
            indeterminate={selection?.isIndeterminate || false}
            onChange={selection?.onSelectAll || (() => {})}
          />
        </div>
      )}
      {columns.map((column) => (
        <div key={column.key} className={`flex items-center ${column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''}`}>
          {column.sortable ? (
            <button 
              onClick={() => onColumnSort(column)}
              className="flex items-center gap-1 hover:text-gray-700 text-left"
            >
              {column.label}
              <SortIcon column={column} sort={sort} />
            </button>
          ) : (
            <span className={column.align === 'center' ? 'text-center' : ''}>{column.label}</span>
          )}
        </div>
      ))}
    </div>
  </div>
));

const PaginationControls = React.memo(({ pagination }: { pagination: DataTablePagination }) => {
  const { 
    currentPage, 
    totalPages, 
    onPageChange, 
    onItemsPerPageChange, 
    itemsPerPageOptions = [10, 25, 50],
    itemsPerPage
  } = pagination;
  
  const paginationInfo = useMemo(() => calculatePaginationInfo(pagination), [pagination]);

  const itemsPerPageDropdownOptions = useMemo(() => 
    itemsPerPageOptions.map(option => ({
      id: option.toString(),
      text: option.toString()
    })), 
    [itemsPerPageOptions]
  );

  return (
    <div className="px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 rounded-b-lg">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Show</span>
        <Dropdown
          size="small"
          variant="clean"
          options={itemsPerPageDropdownOptions}
          value={itemsPerPage.toString()}
          onChange={(option) => onItemsPerPageChange(Number(option.id))}
          className="w-auto"
          buttonClassName="!min-w-0 !justify-start"
          dropdownWidth={120}
        />
        <span>items</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">{paginationInfo.showingText}</span>
        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
});

const BulkActionControls = React.memo(({ 
  bulkActions, 
  selection, 
  selectionStats
}: {
  bulkActions: DataTableBulkActions;
  selection: DataTableSelection<any>;
  selectionStats: ReturnType<typeof getSelectionStats>;
}) => {
  const { selectedCount, totalCount, isAnySelected } = selectionStats;

  const renderActionButton = (actionId: string, actionConfig: DataTableBulkActionButtonConfig) => {
    const restriction = !bulkActions.canPerformActions ? bulkActions.restrictionMessage : null;
    
    const button = (
      <Button
        size="small"
        variant={actionConfig.variant || 'filled'}
        color={actionConfig.color}
        onClick={() => bulkActions.onActionExecute(actionId, selection.selectedItems)}
        disabled={actionConfig.loading || !!restriction}
        icon={actionConfig.loading 
          ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          : actionConfig.icon
        }
        className="w-full sm:w-auto"
      >
        {actionConfig.loading ? actionConfig.loadingText 
          : actionConfig.successText ? actionConfig.successText : actionConfig.text}
      </Button>
    );
    
    return restriction ? <Tooltip text={restriction}>{button}</Tooltip> : button;
  };

  return (
    <div className="w-full sm:w-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">
            {isAnySelected ? `${selectedCount} selected` 
              : `${totalCount} total item${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>
        
        {(() => {
          const restriction = !bulkActions.canPerformActions ? bulkActions.restrictionMessage : null;
          const actionDropdown = (
            <Dropdown
              size="small"
              options={bulkActions.actions}
              value={bulkActions.selectedAction || ''}
              onChange={(option) => bulkActions.onActionChange(option.id)}
              placeholder="Select Action"
              className="w-full sm:w-44"
              disabled={!isAnySelected || !!restriction}
            />
          );
          return restriction ? <Tooltip text={restriction}>{actionDropdown}</Tooltip> : actionDropdown;
        })()}

        {/* Secondary selection dropdown */}
        {bulkActions.selectedAction && bulkActions.requiresSecondarySelection?.has(bulkActions.selectedAction) && (
          <>
            {(() => {
              const restriction = !bulkActions.canPerformActions ? bulkActions.restrictionMessage : null;
              const secondaryDropdown = (
                <Dropdown
                  size="small"
                  options={bulkActions.secondaryOptions || []}
                  value={bulkActions.selectedSecondaryOption || ''}
                  onChange={(option) => bulkActions.onSecondaryOptionChange?.(option.id)}
                  placeholder="Select Option"
                  className="w-full sm:w-44"
                  disabled={!!restriction}
                />
              );
              return restriction ? <Tooltip text={restriction}>{secondaryDropdown}</Tooltip> : secondaryDropdown;
            })()}
            <Button 
              size="small" 
              onClick={() => bulkActions.onActionExecute(bulkActions.selectedAction!, selection.selectedItems)} 
              disabled={!bulkActions.selectedSecondaryOption || !bulkActions.canPerformActions}
              className="w-full sm:w-auto"
            >
              Apply
            </Button>
          </>
        )}

        {/* Custom action buttons */}
        {bulkActions.selectedAction && bulkActions.customActionButtons?.[bulkActions.selectedAction] && 
          renderActionButton(bulkActions.selectedAction, bulkActions.customActionButtons[bulkActions.selectedAction])
        }
      </div>
    </div>
  );
});

const ControlsBar = React.memo(({ 
  hasSearch, 
  hasBulkActions, 
  filters, 
  search, 
  bulkActions, 
  selection, 
  selectionStats
}: {
  hasSearch: boolean;
  hasBulkActions: boolean;
  filters?: ReactNode;
  search?: DataTableSearch;
  bulkActions?: DataTableBulkActions;
  selection?: DataTableSelection<any>;
  selectionStats: ReturnType<typeof getSelectionStats>;
}) => {
  if (!hasSearch && !hasBulkActions && !filters) return null;

  return (
    <div className="space-y-4 px-5 md:px-0">
      {(hasBulkActions || hasSearch) && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {hasBulkActions && bulkActions && selection && (
            <BulkActionControls
              bulkActions={bulkActions}
              selection={selection}
              selectionStats={selectionStats}
            />
          )}
          
          {hasSearch && search && (
            <SearchInput
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
              className={search.className || "w-full sm:w-64"}
            />
          )}
        </div>
      )}
      
      {filters && <div className="w-full">{filters}</div>}
    </div>
  );
});

const TableRow = React.memo(({ 
  item, 
  index, 
  columns, 
  hasSelection, 
  selection, 
  onRowClick, 
  hoverRowClassName, 
  mobileCardClassName, 
  gridCols
}: {
  item: any;
  index: number;
  columns: DataTableColumn<any>[];
  hasSelection: boolean;
  selection?: DataTableSelection<any>;
  onRowClick?: (item: any) => void;
  hoverRowClassName: string;
  mobileCardClassName: string;
  gridCols: string;
}) => {
  const itemId = selection?.getItemId(item) || index.toString();
  const isSelected = selection?.selectedItems.has(itemId) || false;
  
  const handleSelectionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleCheckboxChange = useCallback((checked: boolean) => {
    selection?.onSelectOne(itemId, checked);
  }, [selection, itemId]);

  const handleRowClick = useCallback(() => {
    onRowClick?.(item);
  }, [onRowClick, item]);

  return (
    <div key={itemId}>
      {/* Mobile Layout */}
      <div className={`lg:hidden p-6 ${hoverRowClassName} transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${mobileCardClassName || 'first:rounded-t-lg last:rounded-b-lg'}`} onClick={handleRowClick}>
        <div className="flex items-start gap-3 mb-3">
          {hasSelection && (
            <div onClick={handleSelectionClick}>
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxChange}
              />
            </div>
          )}
          <div className="flex-1">
            {columns.map((column) => {
              if (column.mobileRender) {
                return (
                  <div key={column.key} className="mb-3 last:mb-0">
                    {column.mobileRender(item, index)}
                  </div>
                );
              }
              
              if (column.render) {
                return (
                  <div key={column.key} className="flex items-center mb-3 last:mb-0">
                    {column.mobileLabel && (
                      <span className="text-sm text-gray-500 w-24 shrink-0">{column.mobileLabel}:</span>
                    )}
                    <div className="flex-1">
                      {column.render(item, index)}
                    </div>
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div 
          className={`group ${hoverRowClassName} transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
          onClick={handleRowClick}
        >
          <div className={`grid ${gridCols} px-3 py-3 items-center`}>
            {hasSelection && (
              <div className="flex items-center" onClick={handleSelectionClick}>
                <Checkbox
                  checked={isSelected}
                  onChange={handleCheckboxChange}
                />
              </div>
            )}
            {columns.map((column) => (
              <div key={column.key} className={`${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : ''}`}>
                {column.render ? column.render(item, index) : ''}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

// ====================================
// MAIN COMPONENT
// ====================================

export function DataTable<T>({
  columns,
  data,
  loading = false,
  error,
  successMessage,
  onClearError,
  onClearSuccessMessage,
  emptyMessage = 'No data available',
  pagination,
  selection,
  sort,
  search,
  bulkActions,
  filters,
  onRowClick,
  className = '',
  mobileCardClassName = '',
  hoverRowClassName = 'hover:bg-[rgb(244,246,255)]',
  gridCols,
  totalItemsCount,
}: DataTableProps<T>) {
  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage && onClearSuccessMessage) {
      const timer = setTimeout(() => {
        onClearSuccessMessage();
      }, 5000); // 5 seconds for success messages
      return () => clearTimeout(timer);
    }
  }, [successMessage, onClearSuccessMessage]);

  useEffect(() => {
    if (error && onClearError) {
      const timer = setTimeout(() => {
        onClearError();
      }, 3000); // 3 seconds for error messages
      return () => clearTimeout(timer);
    }
  }, [error, onClearError]);

  // Memoized flags
  const flags = useMemo(() => ({
    hasSelection: !!selection,
    hasPagination: !!pagination,
    hasSearch: !!search,
    hasBulkActions: !!bulkActions && !!selection,
  }), [selection, pagination, search, bulkActions]);

  // Memoized grid columns
  const computedGridCols = useMemo(() => 
    generateGridCols(columns, flags.hasSelection, gridCols), 
    [columns, flags.hasSelection, gridCols]
  );

  // Memoized selection statistics
  const selectionStats = useMemo(() => 
    getSelectionStats(selection, data, totalItemsCount), 
    [selection, data, totalItemsCount]
  );

  // Memoized column sort handler
  const handleColumnSort = useCallback((column: DataTableColumn<T>) => {
    if (!column.sortable || !sort) return;
    const sortKey = column.sortKey || column.key;
    sort.onSort(sortKey);
  }, [sort]);

  // Check if select all notification should be shown and determine state
  const selectAllNotificationState = useMemo(() => {
    if (!selection || !totalItemsCount || totalItemsCount <= data.length) {
      return { show: false, allSelected: false, currentPageSelectedCount: 0 };
    }
    
    const currentPageItemIds = data.map(item => selection.getItemId(item) || '');
    const currentPageSelectedCount = currentPageItemIds.filter(id => selection.selectedItems.has(id)).length;
    const allCurrentPageSelected = currentPageItemIds.length > 0 && 
      currentPageItemIds.every(id => selection.selectedItems.has(id));
    
    if (!allCurrentPageSelected) {
      return { show: false, allSelected: false, currentPageSelectedCount };
    }
    
    // Check if all items across all pages are selected
    const allItemsSelected = selection.selectedItems.size === totalItemsCount;
    
    return { show: true, allSelected: allItemsSelected, currentPageSelectedCount };
  }, [selection, totalItemsCount, data]);

  // Handle select all/clear selection action
  const handleSelectAllToggle = useCallback(() => {
    if (selectAllNotificationState.allSelected) {
      selection?.onSelectAll(false); // Clear selection
    } else {
      selection?.onSelectAll(true); // Select all
    }
  }, [selection, selectAllNotificationState.allSelected]);

  // Show loading state
  if (loading) {
    return <LoadingSkeleton hasSelection={flags.hasSelection} />;
  }

  // Show controls bar
  const controlsBar = (
    <ControlsBar
      hasSearch={flags.hasSearch}
      hasBulkActions={flags.hasBulkActions}
      filters={filters}
      search={search}
      bulkActions={bulkActions}
      selection={selection}
      selectionStats={selectionStats}
    />
  );

  const hasControlsBar = flags.hasSearch || flags.hasBulkActions || !!filters;

  return (
    <div className={className}>
      {/* Controls Bar */}
      {controlsBar}
      
      {/* Table Container */}
      <div className={`bg-white overflow-hidden border-y md:border md:rounded-lg border-gray-200 overflow-x-auto ${hasControlsBar ? 'mt-4' : ''}`} style={{ overflow: 'visible' }}>
        {/* Desktop Header */}
        <TableHeader
          columns={columns}
          hasSelection={flags.hasSelection}
          selection={selection}
          sort={sort}
          gridCols={computedGridCols}
          onColumnSort={handleColumnSort}
        />

        {/* Success Message */}
        {successMessage && (
          <div className="text-sm text-green-800 bg-green-50 p-3 border-b border-gray-200">
            <p className="font-medium">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 border-b border-gray-200">
            <p className="font-medium">{error}</p>
          </div>
        )}

        {/* Select All Notification */}
        {selectAllNotificationState.show && (
          <div className="text-sm text-blue-800 bg-blue-50 p-3 border-b border-gray-200">
            <span className="mr-2">
              {selectAllNotificationState.allSelected 
                ? `All ${totalItemsCount} items selected.`
                : `${selectAllNotificationState.currentPageSelectedCount} of ${data.length} items on this page selected.`
              }
            </span>
            <button
              onClick={handleSelectAllToggle}
              className="text-blue-600 hover:text-blue-800 underline font-medium"
            >
              {selectAllNotificationState.allSelected 
                ? 'Clear selection'
                : `Select all ${totalItemsCount} items`
              }
            </button>
          </div>
        )}

        {/* Table Body */}
        {data.length > 0 ? (
          <div className={`divide-y divide-gray-200 min-h-[240px] lg:min-h-[192px] ${!flags.hasPagination ? 'rounded-b-lg' : ''}`}>
            {data.map((item, index) => (
              <TableRow
                key={selection?.getItemId(item) || index}
                item={item}
                index={index}
                columns={columns}
                hasSelection={flags.hasSelection}
                selection={selection}
                onRowClick={onRowClick}
                hoverRowClassName={hoverRowClassName}
                mobileCardClassName={mobileCardClassName}
                gridCols={computedGridCols}
              />
            ))}
          </div>
        ) : (
          <div className="py-12 min-h-[240px] lg:min-h-[192px] flex items-center justify-center rounded-b-lg">
            <Empty message={emptyMessage} />
          </div>
        )}

        {/* Pagination */}
        {flags.hasPagination && pagination && <PaginationControls pagination={pagination} />}
      </div>
    </div>
  );
} 